import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'

import { AuthContext } from '@/context/auth-context'
import { AlertErrorCircle, ChevronDown } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { RecentActivityCard } from '@/components/RecentActivityCard/RecentActivityCard'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import ConversationDeleteConfirmDialog from '@/chat-common/components/dialogs/ConversationDeleteConfirmDialog'
import ChatTitleChangeDialog from '@/chat-common/components/dialogs/ChatTitleChangeDialog'
import type { AgentConversation } from '@/agent/conversations/store/slice'
import { buildChatFeaturePath, buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { getConversationTimestamp } from '@/chat-common/conversation-navigation/util/get-conversation-timestamp'
import type { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { downloadAgentConversationAsDocx } from '@/agent/conversations/util/DownloadAgentConversation'
import useAssignConversationMatter from '@/matter/hooks/useAssignConversationMatter'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { kMattersRoute, kUntitledMatterLabel } from '@/matter/constants'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'
import { selectDraftsForConversation } from '@/drafting/store/selectors'
import { useNavigateToDraftInChat } from '@/drafting/hooks/useNavigateToDraftInChat'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import { selectMatterMembers } from '@/collaboration/store/selectors'

import { useIsConversationOwner } from '@/agent/conversations/hooks/useIsConversationOwner'

import { useRecentMatterActivity } from './useRecentMatterActivity'
import { buildMemberNamesById, getConversationOwnerLabel } from './conversation-owner-display'

function formatConversationDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('default', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Converts a conversation to a ChatListSidebarItem
 * @param chat - The conversation to convert
 * @returns The ChatListSidebarItem
 */
function chatToSidebarItem(chat: AgentConversation): ChatListSidebarItem {
  return {
    id: chat.id,
    title: chat.title,
    matter_id: chat.matter_id,
    modified_on: getConversationTimestamp(chat),
  }
}

interface RecentActivityItemProps {
  chat: AgentConversation
  matterId: string | null
  onDelete: (item: ChatListSidebarItem) => void
  onRename: (item: ChatListSidebarItem) => void
  /** Owner label for the metadata row (e.g. "Me" or a member's name); undefined shows nothing. */
  owner?: string
}

function RecentActivityItem({ chat, matterId, onDelete, onRename, owner }: RecentActivityItemProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const sidebarItem = chatToSidebarItem(chat)

  // Read-only follows author-ownership (flag-gated), independent of the owner label: a non-owner gets
  // Download only, and the "View Only" tag. The backend enforces the same author-only write access.
  const readonly = !useIsConversationOwner(chat.id)

  const { assignMatter } = useAssignConversationMatter({ conversationId: chat.id })

  const agentConversation = useAppSelector((state: RootState) => selectAgentConversationFromId(state, { conversationId: chat.id }))

  const drafts = useAppSelector((state: RootState) => selectDraftsForConversation(state, { matterId, conversationId: chat.id }))
  const navigateToDraftInChat = useNavigateToDraftInChat()

  const handleDownload = useCallback(() => {
    if (agentConversation) {
      downloadAgentConversationAsDocx(agentConversation)
    }
  }, [agentConversation])

  const handleMatterSelect = useCallback(
    async (toMatterId: string, toMatterName?: string) => {
      if (toMatterId === chat.matter_id) return

      try {
        await assignMatter(toMatterId, () => {
          dispatch(
            openGlobalToast({
              type: GlobalToastType.SUCCESS,
              message: `Conversation moved to ${toMatterName ?? kUntitledMatterLabel}`,
              durationMs: kToastDurationMediumMs,
              withLink: {
                label: 'Go to Chat',
                href: buildConversationPath(toMatterId, chat.id),
              },
              truncateAtLine: 2,
            })
          )
        })
      } catch (error) {
        console.error('Failed to move conversation from RecentMatterActivity:', error)
        dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: 'Error while moving conversation',
            durationMs: kToastDurationShortMs,
          })
        )
      }
    },
    [chat.id, chat.matter_id, assignMatter, dispatch]
  )

  const timestamp = getConversationTimestamp(chat)

  // Matter context required for navigation
  if (!matterId) {
    return null
  }

  const path = buildConversationPath(matterId, chat.id)

  return (
    <RecentActivityCard
      matterId={chat.matter_id ?? null}
      date={formatConversationDate(timestamp)}
      drafts={drafts}
      onClick={() => navigate(path)}
      // A non-owner (read-only viewer) gets Download only; Delete/Rename/Move-to-matter mutate the chat.
      onDelete={readonly ? undefined : () => onDelete(sidebarItem)}
      onDownload={handleDownload}
      onDraftClick={(artifactVersionId) =>
        navigateToDraftInChat({ artifactVersionId, conversationId: chat.id, draftEntryPoint: DraftEntryPoint.ChatHistoryChip })
      }
      onMoveToMatter={readonly ? undefined : handleMatterSelect}
      onRename={readonly ? undefined : () => onRename(sidebarItem)}
      owner={owner}
      readonly={readonly}
      title={chat.title}
    />
  )
}

function SectionHeading() {
  return <h4 className="font-rethink-sans text-base font-medium leading-6 tracking-normal text-brand-neutral-950">Chat History</h4>
}

export function RecentMatterActivity() {
  const navigate = useNavigate()
  const { matterId, activity, hasMore, handleLoadMore, isLoadingWithoutCachedActivity, isError, refetch } = useRecentMatterActivity()
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  const isMatterUnassigned = isUnassignedMatter(matter)
  const { user } = useContext(AuthContext)
  const currentUserId = user?.uid

  // Owner label and view-only tags: only on a shared matter (chats can belong to different members).
  const isSharedMatter = (matter?.member_count ?? 1) > 1
  const showConversationOwners = isSharedMatter
  const members = useAppSelector((state: RootState) => selectMatterMembers(state, showConversationOwners ? matterId ?? undefined : undefined))
  const memberNamesById = useMemo(() => buildMemberNamesById(members), [members])

  // Show the skeleton as soon as the fetch starts and hold it for a short minimum, so a fast fetch doesn't introduce a jarring flash
  const withinMinimumDisplay = useDelayedVisibility(isLoadingWithoutCachedActivity, { showDelayMs: 0, minDisplayMs: 500 })
  const showLoadingSkeletons = isLoadingWithoutCachedActivity || withinMinimumDisplay

  // Show the error state only when the fetch failed with nothing to fall back on
  // A failed background refresh that still has cached chats keeps showing them (matches the loading behavior)
  const showError = isError && activity.length === 0 && !showLoadingSkeletons

  const emptyStateMessage = isMatterUnassigned ? 'No unassigned chats to file' : 'No chats in this matter yet'

  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isPinned, setIsPinned] = useState(false)

  // Use hasActivity as effect dep so observer sets up after data loads, not on initial mount
  const hasActivity = !showLoadingSkeletons && activity.length > 0

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    // When the sentinel scrolls out of view, the sticky header has reached the top and needs a background colour
    const observer = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 1 })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasActivity])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteDialogItem, setDeleteDialogItem] = useState<ChatListSidebarItem | null>(null)

  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameDialogItem, setRenameDialogItem] = useState<ChatListSidebarItem | null>(null)

  const openDeleteDialog = useCallback((item: ChatListSidebarItem) => {
    setDeleteDialogItem(item)
    setDeleteDialogOpen(true)
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
    setDeleteDialogItem(null)
  }, [])

  const openRenameDialog = useCallback((item: ChatListSidebarItem) => {
    setRenameDialogItem(item)
    setRenameDialogOpen(true)
  }, [])

  const closeRenameDialog = useCallback(() => {
    setRenameDialogOpen(false)
    setRenameDialogItem(null)
  }, [])

  return (
    <div className="mt-8 flex min-h-0 flex-1 flex-col pb-6">
      {/* Dialogs to support actions on Recent Activity items */}
      <ConversationDeleteConfirmDialog chatItem={deleteDialogItem} dialogVisible={deleteDialogOpen} onDialogClose={closeDeleteDialog} />
      <ChatTitleChangeDialog chatItem={renameDialogItem} dialogVisible={renameDialogOpen} onDialogClose={closeRenameDialog} />

      {/* Section for showing Recent Activity */}
      {showLoadingSkeletons ? (
        <div>
          <div className="px-4 pt-2 pb-3 border-b border-brand-neutral-200">
            <SectionHeading />
          </div>
          <div>
            {[1, 2, 3, 4].map((i) => (
              <RecentActivityCard key={i} loading />
            ))}
          </div>
        </div>
      ) : activity.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {!isMatterUnassigned && (
            <div className="px-4 pt-2 pb-3">
              <SectionHeading />
            </div>
          )}
          <div className="mx-4 flex min-h-0 min-w-0 flex-1 flex-col rounded-lg border border-brand-neutral-200 bg-brand-neutral-100">
            <div className="flex flex-1 flex-col items-center justify-center py-12 text-center">
              {showError ? (
                <div className="flex flex-col items-center gap-2">
                  <span className="flex items-center gap-1.5 text-sm leading-5 tracking-[-0.15px] text-true-gray-700">
                    <AlertErrorCircle className="size-3.5 shrink-0" />
                    There was an error loading your chats
                  </span>
                  <Button variant="outlined" size="xs" onClick={refetch}>
                    Reload chats
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <p className="text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-600">{emptyStateMessage}</p>
                  {(isMatterUnassigned || matterId) && (
                    <button
                      className="text-sm font-medium tracking-[-0.15px] text-brand-700 underline transition-colors duration-150 hover:text-brand-800 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                      onClick={() => navigate(isMatterUnassigned ? kMattersRoute : buildChatFeaturePath(matterId!))}
                      type="button"
                    >
                      {isMatterUnassigned ? 'Back to all matters' : 'Start a new chat'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Sentinel for detecting when the header becomes sticky; -mt-px cancels its height so it takes no space */}
          <div ref={sentinelRef} className="-mt-px h-px" />
          {/* Background colour only present when pinned to the top, to prevent scrolling content from peeking through */}
          <div className={clsx('sticky top-0 z-[9] px-4 pt-2 pb-3 border-b border-brand-neutral-200', isPinned && 'bg-brand-neutral-50')}>
            <SectionHeading />
          </div>
          <div>
            {activity.map((chat) => {
              const owner = getConversationOwnerLabel({
                conversationUserId: chat.user_id,
                currentUserId,
                memberNamesById,
                enabled: showConversationOwners,
              })
              return <RecentActivityItem key={chat.id} chat={chat} matterId={matterId} onDelete={openDeleteDialog} onRename={openRenameDialog} owner={owner} />
            })}
            {hasMore && (
              <div className="flex justify-center py-4">
                <Button variant="plain" color="neutral" size="sm" onClick={handleLoadMore} trailingIcon={<ChevronDown className="h-4 w-4" />} type="button">
                  Load more
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
